import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, TrendingUp, Users, Award, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Trust & Security",
      description: "Your financial security is our top priority. We employ the latest security measures to protect your assets."
    },
    {
      icon: TrendingUp,
      title: "Innovation",
      description: "We continuously innovate to provide you with cutting-edge banking solutions that meet modern needs."
    },
    {
      icon: Users,
      title: "Customer Focus",
      description: "Every decision we make is centered around providing exceptional service and support to our clients."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from product quality to customer experience."
    }
  ];

  const milestones = [
    { year: "1998", title: "Founded", description: "Morgan Guaranty Trust was established with a vision to revolutionize banking." },
    { year: "2005", title: "Digital Banking", description: "Launched our first online banking platform, making banking accessible 24/7." },
    { year: "2015", title: "Mobile App", description: "Introduced mobile banking app with advanced security features." },
    { year: "2023", title: "AI Integration", description: "Implemented AI-powered financial advisory and customer service." }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">About Morgan Guaranty Trust</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Building a legacy of trust, innovation, and financial excellence for over 25 years
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                At Morgan Guaranty Trust, our mission is to empower individuals and businesses to achieve their financial goals through innovative, secure, and personalized banking solutions.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We believe that everyone deserves access to world-class financial services that are built on trust, transparency, and integrity. Our commitment to excellence drives us to continuously improve and adapt to the changing needs of our clients.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Deliver exceptional customer service and support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Maintain the highest standards of security and compliance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Foster long-term relationships based on trust and mutual success</span>
                </li>
              </ul>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/9504272/pexels-photo-9504272.jpeg"
                alt="Bank Building"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              These principles guide everything we do and shape our commitment to excellence
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="bg-white hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Key milestones that have shaped Morgan Guaranty Trust
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200 hidden lg:block"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex flex-col lg:flex-row gap-8 items-center ${index % 2 === 0 ? '' : 'lg:flex-row-reverse'}`}>
                  <div className="flex-1 lg:text-right">
                    {index % 2 === 0 && (
                      <div className="bg-blue-50 p-6 rounded-xl">
                        <div className="text-blue-600 font-bold text-2xl mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </div>
                    )}
                  </div>
                  <div className="w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg z-10"></div>
                  <div className="flex-1">
                    {index % 2 !== 0 && (
                      <div className="bg-blue-50 p-6 rounded-xl">
                        <div className="text-blue-600 font-bold text-2xl mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Journey?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust Morgan Guaranty Trust for their financial needs
          </p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
              Contact Us Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
